/**
 * Categorical-only analytics helpers for the data-profile feature.
 *
 * All payloads are limited to engine, mode, action, and small numbers/booleans
 * — never table names, column names, values, or anything derived from the
 * user's data. Matches the hygiene of the rest of the codebase
 * (`AnalyticsEventData = Record<string, string | number | boolean>`).
 *
 * Centralized here (instead of inlining `useAnalytics()` everywhere) so the
 * navigation service — which isn't a React component — can fire events too,
 * and so the column type categorization stays in one place.
 */

import { useConnectionStore } from '../../../connections/store/connectionStore';
import { classifyColumnType } from '../utils/profilingSql';
import type { AnalyticsEventData } from '../../../../core/analytics/analyticsTypes';

export type DataProfileEventName =
  | 'feature:data_profile_open'
  | 'feature:data_profile_popover_open'
  | 'feature:data_profile_column_change'
  | 'feature:data_profile_filter_change'
  | 'feature:data_profile_row_limit_change'
  | 'feature:data_profile_histogram_mode'
  | 'feature:data_profile_histogram_axis'
  | 'feature:data_profile_histogram_yscale'
  | 'feature:data_profile_topn_change'
  | 'feature:data_profile_copy_markdown'
  | 'feature:data_profile_mode_change';

type RawValue = string | number | boolean | undefined | null;

/**
 * Fire-and-forget. Drops `undefined` / `null` keys so callers can pass an
 * `engine?` they may or may not have looked up.
 */
export function trackDataProfileEvent(
  eventName: DataProfileEventName,
  data?: Record<string, RawValue>,
): void {
  if (typeof window === 'undefined') return;
  if (!window.electronAPI?.analytics?.track) return;

  const cleaned: AnalyticsEventData = {};
  if (data) {
    for (const [k, v] of Object.entries(data)) {
      if (v === undefined || v === null) continue;
      cleaned[k] = v;
    }
  }
  window.electronAPI.analytics.track(eventName, cleaned).catch(() => {
    // Silently ignore — analytics must not affect UX.
  });
}

/** Look up the engine for a connection id from the connection store. */
export function lookupEngine(connectionId: string | undefined): string | undefined {
  if (!connectionId) return undefined;
  const { activeConnections } = useConnectionStore.getState();
  return activeConnections.find((c) => c.id === connectionId)?.connection.engine;
}

/** Map a SQL data type string to the categorical bucket the profiler uses. */
export function categorizeProfileDataType(dataType: string | undefined): string {
  if (!dataType) return 'unknown';
  return classifyColumnType(dataType);
}
